import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CheckCircle, ArrowRight, Loader2, PartyPopper } from 'lucide-react';
import { getPlanById } from '@/config/pricingPlans';
import { trackCheckoutCompleted } from '@/utils/analytics';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  
  const planId = searchParams.get('plan');
  const sessionId = searchParams.get('session_id');
  const plan = getPlanById(planId);

  useEffect(() => {
    const verifySubscription = async () => {
      try {
        // Track successful checkout
        if (plan) {
          trackCheckoutCompleted(plan.id, 'completed', plan.pricing.monthlyAmount);
        }
        
        // Verify subscription status
        const token = localStorage.getItem('session_token');
        if (token) {
          const response = await axios.get(`${API}/subscriptions/status`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          setSubscription(response.data);
        }
      } catch (err) {
        console.error('Error verifying subscription:', err);
      } finally {
        setLoading(false);
      }
    };
    
    // Small delay to ensure Stripe webhook has processed
    const timer = setTimeout(verifySubscription, 2000);
    return () => clearTimeout(timer);
  }, [plan]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: '#102862' }} />
          <p style={{ color: '#70767C' }}>Activating your subscription...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-6">
      <div className="max-w-lg mx-auto text-center">
        <Card className="p-8 bg-white shadow-lg">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          
          <h1 className="text-2xl font-bold mb-3" style={{ color: '#092036' }}>
            Welcome to {plan?.name || 'Oral Surgery Success'}! 🎉
          </h1>
          
          <p className="mb-6" style={{ color: '#70767C' }}>
            Your subscription is now active. You have full access to all the features included in your plan.
          </p>

          {/* What's Included Summary */}
          {plan && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <h3 className="font-semibold mb-2" style={{ color: '#092036' }}>
                Your {plan.name} includes:
              </h3>
              <ul className="space-y-1">
                {plan.bullets.slice(0, 4).map((bullet, idx) => (
                  <li key={idx} className="text-sm flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-green-600" />
                    <span style={{ color: '#4B5563' }}>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-4">
            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full py-5 text-base font-semibold rounded-xl text-white"
              style={{ backgroundColor: '#102862' }}
            >
              Go to Dashboard
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            
            {planId === 'curbside-consult' && (
              <Button
                onClick={() => navigate('/dashboard?tab=curbside-consult')}
                variant="outline"
                className="w-full py-5 text-base font-semibold rounded-xl"
                style={{ borderColor: '#FF6B35', color: '#FF6B35' }}
              >
                Submit Your First Case
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            )}

            {planId === 'ce-membership' && (
              <Button
                onClick={() => navigate('/courses')}
                variant="outline"
                className="w-full py-5 text-base font-semibold rounded-xl"
                style={{ borderColor: '#FF6B35', color: '#FF6B35' }}
              >
                Browse Courses
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            )}
          </div>
        </Card>

        <p className="text-sm mt-6" style={{ color: '#70767C' }}>
          A confirmation email has been sent to your registered email address.
          <br />
          <Link to="/dashboard" className="underline hover:no-underline">
            Manage your subscription
          </Link>
        </p>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
